import type { components } from "../../shared/api/generated/trading-engine.js";

type DecisionTreeResource = components["schemas"]["BotDecisionTree"];

export const decisionGraphActions = [
  "hold",
  "enter-long",
  "exit-long",
  "enter-short",
  "exit-short",
  "reduce-long",
  "reduce-short",
] as const;
export type DecisionGraphAction = (typeof decisionGraphActions)[number];

export type DecisionGraphNodeDraft =
  | {
      readonly action: DecisionGraphAction;
      readonly closeFraction?: number;
      readonly id: string;
      readonly kind: "action";
      readonly label?: string;
      readonly stop?: unknown;
      readonly target?: unknown;
    }
  | {
      readonly condition: unknown;
      readonly id: string;
      readonly kind: "decision";
      readonly label?: string;
      readonly onFalse: string;
      readonly onTrue: string;
    };

export interface DecisionGraphParametersDraft {
  readonly calendar?: Readonly<Record<string, unknown>>;
  readonly entryNodeId: string;
  readonly nodes: readonly DecisionGraphNodeDraft[];
}

export interface DecisionTreeIndicatorView {
  readonly interval: string;
  readonly parameters: readonly string[];
  readonly type: string;
}

export interface DecisionTreeRuleView {
  readonly children: readonly DecisionTreeRuleView[];
  readonly condition?: string;
  readonly field?: string;
  readonly label: string;
  readonly operator?: string;
  readonly value?: string;
}

export interface DecisionTreeActionView {
  readonly actionName: string;
  readonly closeFraction?: number;
  readonly orderType?: string;
  readonly stop?: string;
  readonly target?: string;
}

export interface DecisionTreeNodeView {
  readonly action?: DecisionTreeActionView;
  readonly id: string;
  readonly label: string;
  readonly rule?: DecisionTreeRuleView;
  readonly type: "action" | "query" | "start" | "unknown";
}

export interface DecisionTreeEdgeView {
  readonly condition?: string;
  readonly id: string;
  readonly source: string;
  readonly target: string;
}

export interface BotDecisionTreeView {
  readonly calendar?: Readonly<Record<string, unknown>>;
  readonly botId: string;
  readonly botVersion: number;
  readonly edges: readonly DecisionTreeEdgeView[];
  readonly indicators: readonly DecisionTreeIndicatorView[];
  readonly name: string;
  readonly nodes: readonly DecisionTreeNodeView[];
  readonly revision: number;
  readonly revisionId: string;
  readonly source: "active-revision";
  readonly symbol: string;
}

function record(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function text(value: unknown, fallback = ""): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return fallback;
}

function isAction(value: string): value is DecisionGraphAction {
  return decisionGraphActions.includes(value as DecisionGraphAction);
}

export function parseDecisionGraphParameters(value: unknown): DecisionGraphParametersDraft | null {
  const parameters = record(value);
  const entryNodeId = text(parameters.entryNodeId);
  if (entryNodeId === "" || !Array.isArray(parameters.nodes)) return null;
  const nodes = parameters.nodes.flatMap((candidate): readonly DecisionGraphNodeDraft[] => {
    const node = record(candidate);
    const id = text(node.id);
    const kind = text(node.kind);
    const label = text(node.label);
    if (id === "") return [];
    if (kind === "action") {
      const action = text(node.action);
      if (!isAction(action)) return [];
      const closeFraction =
        typeof node.closeFraction === "number" && Number.isFinite(node.closeFraction)
          ? node.closeFraction
          : undefined;
      return [
        {
          action,
          id,
          kind,
          ...(closeFraction === undefined ? {} : { closeFraction }),
          ...(label === "" ? {} : { label }),
          ...(node.stop === undefined ? {} : { stop: node.stop }),
          ...(node.target === undefined ? {} : { target: node.target }),
        },
      ];
    }
    if (kind === "decision") {
      const onFalse = text(node.onFalse);
      const onTrue = text(node.onTrue);
      if (onFalse === "" || onTrue === "") return [];
      return [
        {
          condition: node.condition,
          id,
          kind,
          ...(label === "" ? {} : { label }),
          onFalse,
          onTrue,
        },
      ];
    }
    return [];
  });
  return {
    entryNodeId,
    nodes,
    ...(parameters.calendar === undefined ? {} : { calendar: record(parameters.calendar) }),
  };
}

function fieldLabel(value: unknown): string {
  const field = record(value);
  const kind = text(field.kind);
  const offset = typeof field.offset === "number" && field.offset > 0 ? `[−${field.offset}]` : "";
  if (kind === "bar") return `${text(field.field, "bar")}${offset}`;
  if (kind === "indicator") {
    const parameters = record(field.parameters);
    const parameterText = Object.entries(parameters)
      .map(([key, item]) => `${key}=${text(item, "?")}`)
      .join(", ");
    return `${text(field.indicator, "indicator")}(${parameterText}).${text(field.output, "value")}${offset}`;
  }
  if (kind === "named-stock-field") return `${text(field.name, "stock field")}${offset}`;
  return "unknown field";
}

function expressionLabel(value: unknown): string {
  const expression = record(value);
  const kind = text(expression.kind);
  if (kind === "constant") return text(expression.value, "?");
  if (kind === "monthly-value")
    return expression.value === "close"
      ? "Completed month-end close"
      : "Monthly SMA (calendar lookback)";
  if (kind === "field") return fieldLabel(expression.field);
  if (kind === "unary")
    return `${text(expression.operator)}(${expressionLabel(expression.operand)})`;
  if (kind === "binary") {
    return `(${expressionLabel(expression.left)} ${text(expression.operator)} ${expressionLabel(expression.right)})`;
  }
  return "unknown expression";
}

function conditionView(value: unknown): DecisionTreeRuleView {
  const condition = record(value);
  const kind = text(condition.kind);
  if (kind === "monthly-history-ready")
    return { children: [], label: "Consecutive month-end history ready" };
  if (kind === "all" || kind === "any") {
    return {
      children: Array.isArray(condition.conditions)
        ? condition.conditions.map((child) => conditionView(child))
        : [],
      condition: kind,
      label: kind === "all" ? "All conditions" : "Any condition",
    };
  }
  if (kind === "not") {
    return {
      children: [conditionView(condition.condition)],
      condition: "not",
      label: "Not",
    };
  }
  if (kind === "position") {
    return {
      children: [],
      field: "position",
      label: "Position state",
      operator: text(condition.operator),
      value: text(condition.value),
    };
  }
  if (kind === "comparison") {
    return {
      children: [],
      field: expressionLabel(condition.left),
      label: "Comparison",
      operator: text(condition.operator),
      value: expressionLabel(condition.right),
    };
  }
  return { children: [], label: "Invalid condition" };
}

function collectIndicators(value: unknown, output: Map<string, DecisionTreeIndicatorView>): void {
  if (Array.isArray(value)) {
    for (const item of value) collectIndicators(item, output);
    return;
  }
  const item = record(value);
  if (text(item.kind) === "indicator") {
    const type = text(item.indicator);
    const parameters = Object.entries(record(item.parameters)).map(
      ([key, parameter]) => `${key}=${text(parameter, "?")}`,
    );
    output.set(`${type}:${text(item.output)}:${parameters.join(",")}`, {
      interval: "Bot run interval",
      parameters,
      type,
    });
  }
  for (const child of Object.values(item)) collectIndicators(child, output);
}

export function decisionGraphView(
  parameters: DecisionGraphParametersDraft,
  context: {
    readonly botId?: string;
    readonly botVersion?: number;
    readonly name?: string;
    readonly orderType?: string;
    readonly revision?: number;
    readonly revisionId?: string;
    readonly symbol?: string;
  } = {},
): BotDecisionTreeView {
  const entryId = "__entry__";
  const indicators = new Map<string, DecisionTreeIndicatorView>();
  collectIndicators(parameters, indicators);
  return {
    botId: context.botId ?? "",
    ...(parameters.calendar === undefined ? {} : { calendar: parameters.calendar }),
    botVersion: context.botVersion ?? 0,
    edges: [
      {
        id: `${entryId}->${parameters.entryNodeId}`,
        source: entryId,
        target: parameters.entryNodeId,
      },
      ...parameters.nodes.flatMap((node): readonly DecisionTreeEdgeView[] =>
        node.kind === "decision"
          ? [
              {
                condition: "Yes",
                id: `${node.id}:true:${node.onTrue}`,
                source: node.id,
                target: node.onTrue,
              },
              {
                condition: "No",
                id: `${node.id}:false:${node.onFalse}`,
                source: node.id,
                target: node.onFalse,
              },
            ]
          : [],
      ),
    ],
    indicators: [...indicators.values()],
    name: context.name ?? "Decision graph",
    nodes: [
      {
        id: entryId,
        label:
          parameters.calendar?.kind === "month-end"
            ? `Month-end · ${text(parameters.calendar.lookbackMonths)} months · next session open`
            : "Start",
        type: "start",
      },
      ...parameters.nodes.map((node): DecisionTreeNodeView =>
        node.kind === "action"
          ? {
              action: {
                actionName: node.action,
                ...(node.closeFraction === undefined ? {} : { closeFraction: node.closeFraction }),
                ...(context.orderType === undefined ? {} : { orderType: context.orderType }),
                ...(node.stop === undefined ? {} : { stop: expressionLabel(node.stop) }),
                ...(node.target === undefined ? {} : { target: expressionLabel(node.target) }),
              },
              id: node.id,
              label: node.label ?? node.id,
              type: "action",
            }
          : {
              id: node.id,
              label: node.label ?? node.id,
              rule: conditionView(node.condition),
              type: "query",
            },
      ),
    ],
    revision: context.revision ?? 0,
    revisionId: context.revisionId ?? "",
    source: "active-revision",
    symbol: context.symbol ?? "",
  };
}

export function botDecisionTreeView(resource: DecisionTreeResource): BotDecisionTreeView {
  const strategy = record(resource.strategy);
  const execution = record(resource.execution);
  const parameters = parseDecisionGraphParameters(strategy.parameters);
  return decisionGraphView(parameters ?? { entryNodeId: "invalid", nodes: [] }, {
    botId: resource.botId,
    botVersion: resource.botVersion,
    name: resource.name,
    orderType: text(execution.defaultOrderType),
    revision: resource.revision,
    revisionId: resource.revisionId,
    symbol: resource.symbol,
  });
}
